import React, { useEffect, useState } from "react";

import { createBrowserRouter, RouterProvider, Route, createRoutesFromElements } from "react-router-dom";

import { initializeConfig, isMaintenance } from "./config/index";
import { initializeProductionMode } from "./utils/productionMode";
import './utils/axiosConfig'; // Initialize axios interceptors

import './assets/css/global.scss';
import './App.scss';

import RootLayout from "./Components/Main/RootLayout";
import ProtectedRoute from "./Components/Main/ProtectedRoute";
import RouterErrorBoundary from "./Components/Main/RouterErrorBoundary";
import { TranslationProvider } from "./contexts/TranslationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GlobalPopupProvider } from "./contexts/GlobalPopupContext";

import Retail from "./Components/Pages/Retail";
import Gallery from "./Components/Pages/Gallery";
import Evidence from "./Components/Pages/Evidence";
import Analyze from "./Components/Pages/Analyze";
import Login from "./Components/Pages/Login";
import ForgotPassword from "./Components/Pages/ForgotPassword";
import Import from "./Components/Pages/Import";
import Profiles from "./Components/Pages/Profiles";
import History from "./Components/Pages/History";
import NotFound from "./Components/Pages/NotFound";
import Details from "./Components/Modals/Details";
import Maintenance from "./Components/Pages/Maintenance";

const App = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [basename, setBasename] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    // Initialize production mode (disable React DevTools, etc.)
    initializeProductionMode();

    const loadConfiguration = async () => {
      try {
        console.log('Initializing application configuration...');
        await initializeConfig();
        console.log('Configuration loaded successfully');

        // Get basename from runtime config
        const configuredBasename = getBasename();

        // // Debug information for alias prefix routing
        // console.log('=== Basename Configuration Debug ===');
        // console.log('Current URL pathname:', window.location.pathname);
        // console.log('Current URL origin:', window.location.origin);
        // console.log('Configured basename:', configuredBasename);
        // console.log('Alias prefix from config:', window.RUNTIME_CONFIG?.deployment?.aliasPrefix);

        // // Validate basename matches current path
        // if (configuredBasename && !window.location.pathname.startsWith(configuredBasename)) {
        //   console.warn('⚠️ Basename mismatch detected!');
        //   console.warn('Current path:', window.location.pathname);
        //   console.warn('Expected basename:', configuredBasename);
        //   console.warn('This may cause routing issues on page refresh.');
        // } else {
        //   console.log('✅ Basename configuration looks correct');
        // }
        // console.log('=====================================');

        // get client IP, if == 14.161.48.x, set maintenance mode false



        setBasename(configuredBasename);

        // Check for maintenance mode

        let maintenanceStatus = isMaintenance();

        if (maintenanceStatus) {
          // check client IP against allowed list when in maintenance mode
          let clientIp = null;
          try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            clientIp = data.ip;
            console.log('Client IP address:', clientIp);
          } catch (ipError) {
            console.warn('Could not fetch client IP address:', ipError);
          }
          if (clientIp) {
            let arrayAllowIPs = window.RUNTIME_CONFIG?.maintenance?.allowIPs || [];
            if (!Array.isArray(arrayAllowIPs)) {
              arrayAllowIPs = [];
            }

            for (let i = 0; i < arrayAllowIPs.length; i++) {
              const allowedIP = arrayAllowIPs[i];
              if (clientIp.startsWith(allowedIP)) {
                maintenanceStatus = false;
                break;
              }
            }
          }
        }

        setMaintenanceMode(maintenanceStatus);

        //console.log('Maintenance mode status:', maintenanceStatus);

        setConfigLoaded(true);
      } catch (error) {
        //console.error('Failed to load configuration:', error);
        // Still allow app to load with default config
        setConfigLoaded(true);
      }
    };

    loadConfiguration();
  }, []);

  // Simple function to get basename from configuration
  const getBasename = () => {
    try {
      // Check for runtime configuration function first
      if (typeof window !== 'undefined' && window.getBasename) {
        const basename = window.getBasename();
        console.log('Basename from window.getBasename():', basename);
        return basename;
      }

      // Fallback to no basename for root deployment
      console.log('No basename configuration found, using root deployment');
      return '';
    } catch (error) {
      console.error('Error in getBasename():', error);
      return '';
    }
  };

  if (!configLoaded) {
    return (
      <div className="app-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Show maintenance page if system is in maintenance mode
  if (maintenanceMode) {
    return (
      <ThemeProvider>
        <TranslationProvider>
          <GlobalPopupProvider>
            <Maintenance />
          </GlobalPopupProvider>
        </TranslationProvider>
      </ThemeProvider>
    );
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<RootLayout />}>

        {/* Public routes */}
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />

        {/* Protected routes */}
        <Route index element={
          <ProtectedRoute>
            <Evidence />
          </ProtectedRoute>
        } />
        <Route path="gallery" element={
          <ProtectedRoute>
            {/* <Gallery /> */}
            <Evidence />
          </ProtectedRoute>
        } />
        <Route path="evidence" element={
          <ProtectedRoute>
            <Evidence />
          </ProtectedRoute>
        } />
        <Route path="retail" element={
          <ProtectedRoute>
            <Retail />
          </ProtectedRoute>
        } />
        <Route path="details" element={
          <ProtectedRoute>
            <Details />
          </ProtectedRoute>
        } />
        {/* details/:id */}
        <Route path="details/:id" element={
          <ProtectedRoute>
            <Details />
          </ProtectedRoute>
        } />
        {/* details/:bucket/:id for gallery type specific routes */}
        <Route path="details/:bucket/:id" element={
          <ProtectedRoute>
            <Details />
          </ProtectedRoute>
        } />

        <Route path="analyze" element={
          <ProtectedRoute>
            <Analyze />
          </ProtectedRoute>
        } />
        <Route path="analyze/:id" element={
          <ProtectedRoute>
            <Analyze />
          </ProtectedRoute>
        } />
        <Route path="import" element={
          <ProtectedRoute>
            <Import />
          </ProtectedRoute>
        } />
        <Route path="profiles" element={
          <ProtectedRoute>
            <Profiles />
          </ProtectedRoute>
        } />
        <Route path="history" element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        } />

        {/* Not found route */}
        <Route path="*" element={<NotFound />} />
      </Route>
    ), {
    basename: basename || undefined // Use the detected basename, fallback to undefined for root
  },
  );

  console.log('Router created with basename:', basename || 'undefined (root)');

  // simple app to check rendering and routing
  return (
    <ThemeProvider>
      <TranslationProvider>
        <GlobalPopupProvider>
          <RouterErrorBoundary>
            <React.Suspense fallback={
              <div className="app-loading">
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading application...</p>
                </div>
              </div>
            }>
              <RouterProvider router={router} />
            </React.Suspense>
          </RouterErrorBoundary>
        </GlobalPopupProvider>
      </TranslationProvider>
    </ThemeProvider>
  );

  // return (
  //   <AuthProvider>
  //     <React.StrictMode>
  //       <React.Suspense>
  //         <InactivityProvider>
  //           <GlobalPopupProvider>
  //             <TranslationProvider>
  //               <Navbar />
  //               <Main />
  //               <Footer />
  //             </TranslationProvider>
  //           </GlobalPopupProvider>
  //         </InactivityProvider>
  //       </React.Suspense>
  //     </React.StrictMode>
  //   </AuthProvider>
  // );
};

export default App;